import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, MapPin, Clock, CheckCircle2, Play, X } from "lucide-react";
import { useSplits } from "@/lib/useSplits";

export default function CalendarPage() {
  const SPLITS = useSplits();
  const [events, setEvents] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [watchUrl, setWatchUrl] = useState("");
  const [now, setNow] = useState(new Date());
  const [openEvent, setOpenEvent] = useState(null);
  // seasonId per split key
  const [seasonBySplit, setSeasonBySplit] = useState({});

  useEffect(() => {
    api.get("/calendar").then(x => setEvents(x.data)).catch(() => {});
    api.get("/seasons").then(x => setSeasons(x.data)).catch(() => {});
    api.get("/settings").then(({ data }) => setWatchUrl(data?.watch_live_url || "")).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Default-Saison pro Split (aktuell oder neueste)
  useEffect(() => {
    if (!seasons.length || !SPLITS.length) return;
    setSeasonBySplit(prev => {
      const next = { ...prev };
      for (const s of SPLITS) {
        if (next[s.key]) continue;
        const inSplit = seasons.filter(x => (x.split || "champions") === s.key);
        if (!inSplit.length) continue;
        const cur = inSplit.find(x => x.is_current) || [...inSplit].sort((a, b) => (b.year || 0) - (a.year || 0))[0];
        if (cur) next[s.key] = cur.id;
      }
      return next;
    });
  }, [seasons, SPLITS]);

  const bySplit = useMemo(() => {
    const m = {};
    for (const s of SPLITS) m[s.key] = [];
    for (const e of events) (m[e.split] || (m[e.split] = [])).push(e);
    return m;
  }, [events, SPLITS]);

  const defaultTab = SPLITS[0]?.key || "champions";
  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-14" data-testid="calendar-page">
      <div className="h-1 w-12 bg-[#00E5FF] mb-4" />
      <h1 className="font-display font-black italic uppercase tracking-normal text-5xl sm:text-7xl leading-none">Rennkalender</h1>
      <p className="text-neutral-400 mt-3 max-w-2xl">Getrennte Kalender pro Split der Liga. Wähle eine Saison, um vergangene Termine einzusehen.</p>

      <Tabs defaultValue={defaultTab} className="mt-10">
        <TabsList className="bg-transparent border border-white/10 p-0 rounded-none flex-wrap h-auto">
          {SPLITS.map((s) => (
            <TabsTrigger key={s.key} value={s.key} data-testid={`cal-tab-${s.key}`}
              className="data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black uppercase tracking-widest font-bold rounded-none px-6 py-2 text-xs">
              {s.name}
            </TabsTrigger>
          ))}
        </TabsList>

        {SPLITS.map((s) => {
          const splitSeasons = seasons.filter(x => (x.split || "champions") === s.key);
          const seasonId = seasonBySplit[s.key] || "";
          const filtered = (bySplit[s.key] || []).filter(e => !seasonId || e.season_id === seasonId);
          return (
            <TabsContent key={s.key} value={s.key} className="mt-8">
              {splitSeasons.length > 0 && (
                <div className="flex items-center gap-3 mb-6" data-testid={`cal-season-wrap-${s.key}`}>
                  <span className="text-xs uppercase tracking-widest text-neutral-400">Saison:</span>
                  <select
                    value={seasonId}
                    onChange={e => setSeasonBySplit(p => ({ ...p, [s.key]: e.target.value }))}
                    className="bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm focus:border-[#00E5FF] outline-none"
                    data-testid={`cal-season-selector-${s.key}`}
                  >
                    {splitSeasons.map(x => (
                      <option key={x.id} value={x.id}>
                        {x.name}{x.game ? ` · ${x.game}` : ""}{x.is_current ? " · aktuell" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <SplitCalendar
                split={s}
                events={filtered}
                now={now}
                watchUrl={watchUrl}
                onEventClick={(e) => e.completed && setOpenEvent(e)}
              />
            </TabsContent>
          );
        })}
      </Tabs>

      {openEvent && (
        <RaceResultsModal event={openEvent} onClose={() => setOpenEvent(null)} />
      )}
    </div>
  );
}

function SplitCalendar({ split, events, now, watchUrl, onEventClick }) {
  const next = useMemo(() => events.find(e => !e.completed && new Date(`${e.date}T${e.time}:00`) > now), [events, now]);

  return (
    <div data-testid={`split-section-${split.key}`}>
      {next ? <NextRaceBanner event={next} split={split} now={now} /> : (
        <div className="card-surface p-6 text-neutral-400">Kein bevorstehendes Rennen in dieser Saison.</div>
      )}

      <div className="mt-8 border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]" data-testid={`cal-table-${split.key}`}>
          <thead className="bg-white/5 text-neutral-400 uppercase text-xs tracking-widest">
            <tr>
              <th className="text-left px-4 py-3 w-16">R.</th>
              <th className="text-left px-4 py-3">Rennen</th>
              <th className="text-left px-4 py-3">Strecke</th>
              <th className="text-left px-4 py-3">Datum</th>
              <th className="text-left px-4 py-3">Zeit</th>
              <th className="text-right px-4 py-3 w-40">Status</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => {
              const isNext = next && next.id === e.id;
              const clickable = e.completed;
              return (
                <tr
                  key={e.id}
                  onClick={() => clickable && onEventClick(e)}
                  className={`border-t border-white/5 transition-colors ${isNext ? "bg-[#00E5FF]/5" : "hover:bg-white/5"} ${clickable ? "cursor-pointer" : ""}`}
                  data-testid={`cal-row-${e.id}`}
                >
                  <td className="px-4 py-3 font-display font-black italic text-lg text-white/80">{String(e.round).padStart(2, "0")}</td>
                  <td className="px-4 py-3 font-bold">
                    <span className="inline-flex items-center flex-wrap gap-2">
                      {e.title}
                      {e.is_sprint && (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 font-bold" style={{ background: "#FFCC00", color: "#000" }} data-testid={`sprint-badge-${e.id}`}>Sprint</span>
                      )}
                      {isNext && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[#00E5FF] text-black font-bold">Nächstes</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-neutral-300">
                    <span className="inline-flex items-center gap-2">
                      {e.country_code && (
                        <img
                          src={`https://flagcdn.com/w40/${e.country_code.toLowerCase()}.png`}
                          alt={e.country_code}
                          className="w-6 h-4 object-cover border border-white/10 shrink-0"
                          draggable={false}
                        />
                      )}
                      {e.track}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-neutral-300">{formatDate(e.date)}</td>
                  <td className="px-4 py-3 text-neutral-300">{e.time}</td>
                  <td className="px-4 py-3 text-right">
                    {e.completed ? (
                      <span className="inline-flex items-center gap-1 text-xs text-neutral-500" data-testid={`status-done-${e.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Gefahren</span>
                    ) : watchUrl ? (
                      <a
                        href={watchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(ev) => ev.stopPropagation()}
                        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold px-3 py-1.5 border transition-colors"
                        style={{ borderColor: "#FF0040", color: "#FF0040" }}
                        data-testid={`watch-live-${e.id}`}
                      >
                        <Play className="w-3.5 h-3.5 fill-current" /> Watch Live
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-neutral-500" title="Watch-Live-Link im Admin → Sonstiges hinterlegen">
                        <Play className="w-3.5 h-3.5" /> Watch Live (noch nicht gesetzt)
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {events.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-neutral-500">Keine Events.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============ Race Results Modal ============ */
function RaceResultsModal({ event, onClose }) {
  const [results, setResults] = useState([]);
  const [drivers, setDrivers] = useState({});
  const [teams, setTeams] = useState({});
  const [tab, setTab] = useState("quali");

  useEffect(() => {
    Promise.all([
      api.get(`/race-results?calendar_event_id=${event.id}`).then(x => x.data).catch(() => []),
      api.get("/drivers").then(x => x.data).catch(() => []),
      api.get("/teams").then(x => x.data).catch(() => []),
    ]).then(([r, d, t]) => {
      setResults(r || []);
      setDrivers(Object.fromEntries((d || []).map(x => [x.id, x])));
      setTeams(Object.fromEntries((t || []).map(x => [x.id, x])));
    });
  }, [event.id]);

  // Esc to close
  useEffect(() => {
    const h = (ev) => { if (ev.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const hasSprint = !!event.is_sprint;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="race-results-modal">
      <div className="bg-[#0B0F19] border border-white/10 max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="border-b border-white/10 p-5 flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            {event.country_code && (
              <img
                src={`https://flagcdn.com/w80/${event.country_code.toLowerCase()}.png`}
                alt={event.country_code}
                className="w-14 h-10 object-cover border border-white/10 mt-1"
                draggable={false}
                data-testid="modal-flag"
              />
            )}
            <div>
              <div className="eyebrow text-[#00E5FF]">R{String(event.round).padStart(2,"0")} · {formatDate(event.date)}</div>
              <div className="font-display font-black italic uppercase text-2xl md:text-3xl mt-1">{event.title}</div>
              <div className="text-xs text-neutral-400 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{event.track}</span>
                <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{event.time} Uhr</span>
                {event.is_sprint && (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 font-bold" style={{ background: "#FFCC00", color: "#000" }}>Sprint-Wochenende</span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost text-xs shrink-0" data-testid="modal-close"><X className="w-4 h-4" /> Schließen</button>
        </div>

        {/* Tabs */}
        <div className="border-b border-white/10 px-5 pt-3 flex flex-wrap gap-1">
          {[["quali","Qualifying"],["race","Rennen"],...(hasSprint?[["sprint","Sprint"]]:[])].map(([k,lbl]) => (
            <button key={k} type="button"
              onClick={() => setTab(k)}
              className={`px-4 py-2 text-xs uppercase font-bold tracking-widest transition-colors ${tab===k ? "bg-[#00E5FF] text-black" : "text-neutral-300 hover:bg-white/5"}`}
              data-testid={`result-tab-${k}`}>
              {lbl}
              {k==="sprint" && <span className="ml-1 px-1 text-[8px]" style={{background:"#FFCC00",color:"#000"}}>S</span>}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-5">
          {results.length === 0 ? (
            <div className="text-center text-neutral-500 py-10">Noch keine Ergebnisse für dieses Rennen erfasst.</div>
          ) : (
            <ResultsTable tab={tab} results={results} drivers={drivers} teams={teams} />
          )}
        </div>
      </div>
    </div>
  );
}

function ResultsTable({ tab, results, drivers, teams }) {
  // Reserve-Fahrer in Fahrerwertung der Tabelle markieren (Hinweis-Stil), aber listen
  const posKey = tab === "quali" ? "qualifying_position" : tab === "sprint" ? "sprint_position" : "finishing_position";
  const sorted = [...results]
    .filter(r => {
      // Zeige nur Zeilen mit irgendeinem Wert für diese Session
      if (tab === "quali") return r.qualifying_position != null || r.quali_time;
      if (tab === "sprint") return r.sprint_position != null || r.sprint_time || r.sprint_points;
      return r.finishing_position != null || r.race_time || r.points;
    })
    .sort((a, b) => {
      const pa = parseInt(a?.[posKey]); const pb = parseInt(b?.[posKey]);
      const va = Number.isFinite(pa) ? pa : Infinity;
      const vb = Number.isFinite(pb) ? pb : Infinity;
      return va - vb;
    });

  // Pink für schnellste Sektoren / beste Runde
  const fastest = fastestPerField(results, tab);
  const isFastest = (field, val) => val && parseT(val) !== Infinity && parseT(val) === fastest[field];
  const PINK = "#F472B6";

  if (sorted.length === 0) {
    return <div className="text-center text-neutral-500 py-8">Keine Daten für diese Session.</div>;
  }

  if (tab === "quali") {
    return (
      <table className="w-full text-sm" data-testid="result-table-quali">
        <thead className="bg-white/5 text-neutral-400 uppercase text-[10px] tracking-widest">
          <tr>
            <th className="text-left px-3 py-2 w-12">Pos</th>
            <th className="text-left px-3 py-2">Fahrer</th>
            <th className="text-left px-3 py-2">Team</th>
            <th className="text-center px-3 py-2">Time</th>
            <th className="text-center px-3 py-2">GAP</th>
            <th className="text-center px-3 py-2">S1</th>
            <th className="text-center px-3 py-2">S2</th>
            <th className="text-center px-3 py-2">S3</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(r => {
            const d = drivers[r.driver_id] || {};
            const tm = teams[r.team_id || d.team_id] || {};
            return (
              <tr key={r.id} className="border-t border-white/5">
                <td className="px-3 py-2 font-display font-black italic text-lg" style={{ color: posColor(r.qualifying_position) }}>{r.qualifying_position ?? "—"}</td>
                <td className="px-3 py-2 font-bold">{d.name || "—"} <span className="text-neutral-500">#{d.number}</span></td>
                <td className="px-3 py-2" style={{ color: tm.color }}>{tm.name || "—"}</td>
                <td className="px-3 py-2 text-center font-mono" style={isFastest("quali_time", r.quali_time) ? { color: PINK, fontWeight: 700 } : undefined}>{r.quali_time || "—"}</td>
                <td className="px-3 py-2 text-center font-mono text-neutral-400">{r.quali_gap || "—"}</td>
                <td className="px-3 py-2 text-center font-mono" style={isFastest("quali_s1", r.quali_s1) ? { color: PINK, fontWeight: 700 } : { color: "#9CA3AF" }}>{r.quali_s1 || "—"}</td>
                <td className="px-3 py-2 text-center font-mono" style={isFastest("quali_s2", r.quali_s2) ? { color: PINK, fontWeight: 700 } : { color: "#9CA3AF" }}>{r.quali_s2 || "—"}</td>
                <td className="px-3 py-2 text-center font-mono" style={isFastest("quali_s3", r.quali_s3) ? { color: PINK, fontWeight: 700 } : { color: "#9CA3AF" }}>{r.quali_s3 || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  const isSpr = tab === "sprint";
  const timeKey = isSpr ? "sprint_time" : "race_time";
  const bestKey = isSpr ? "sprint_best" : "race_best";
  const ptsKey = isSpr ? "sprint_points" : "points";
  const flKey = isSpr ? "sprint_fastest_lap" : "fastest_lap";
  return (
    <table className="w-full text-sm" data-testid={`result-table-${tab}`}>
      <thead className="bg-white/5 text-neutral-400 uppercase text-[10px] tracking-widest">
        <tr>
          <th className="text-left px-3 py-2 w-12">Pos</th>
          <th className="text-left px-3 py-2">Fahrer</th>
          <th className="text-left px-3 py-2">Team</th>
          <th className="text-center px-3 py-2">Zeit</th>
          <th className="text-center px-3 py-2">Best Lap</th>
          <th className="text-center px-3 py-2 w-10">FL</th>
          <th className="text-center px-3 py-2 w-12">DOTD</th>
          <th className="text-center px-3 py-2 w-10">DNF</th>
          <th className="text-right px-3 py-2 w-16">Pts</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map(r => {
          const d = drivers[r.driver_id] || {};
          const tm = teams[r.team_id || d.team_id] || {};
          const pos = r[posKey];
          return (
            <tr key={r.id} className={`border-t border-white/5 ${r.is_reserve ? "bg-amber-500/5" : ""}`}>
              <td className="px-3 py-2 font-display font-black italic text-lg" style={{ color: posColor(pos) }}>{pos ?? (r.dnf ? "DNF" : "—")}</td>
              <td className="px-3 py-2 font-bold">
                {d.name || "—"} <span className="text-neutral-500">#{d.number}</span>
                {r.is_reserve && <span className="ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-500/40 text-amber-400">Reserve</span>}
              </td>
              <td className="px-3 py-2" style={{ color: tm.color }}>{tm.name || "—"}</td>
              <td className="px-3 py-2 text-center font-mono">{r[timeKey] || "—"}</td>
              <td className="px-3 py-2 text-center font-mono" style={isFastest(bestKey, r[bestKey]) ? { color: PINK, fontWeight: 700 } : { color: "#9CA3AF" }}>{r[bestKey] || "—"}</td>
              <td className="px-3 py-2 text-center">{r[flKey] ? <span className="text-[#A855F7] font-bold">FL</span> : ""}</td>
              <td className="px-3 py-2 text-center">{r.dotd ? <span className="text-[#FFCC00] font-bold">★</span> : ""}</td>
              <td className="px-3 py-2 text-center">{r.dnf ? <span className="text-red-500 text-[10px] font-bold">DNF</span> : ""}</td>
              <td className="px-3 py-2 text-right font-bold">{r[ptsKey] || 0}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const posColor = (p) => p === 1 ? "#FFD700" : p === 2 ? "#C0C0C0" : p === 3 ? "#CD7F32" : "#FFFFFF";

const parseT = (s) => {
  if (!s) return Infinity;
  const parts = String(s).trim().split(":");
  let total = 0;
  for (const p of parts) total = total * 60 + parseFloat(p);
  return isNaN(total) || total <= 0 ? Infinity : total;
};

function fastestPerField(rows, tab) {
  const fields = tab === "quali"
    ? ["quali_time", "quali_s1", "quali_s2", "quali_s3"]
    : tab === "sprint"
    ? ["sprint_best"]
    : ["race_best"];
  const result = {};
  for (const f of fields) {
    let best = Infinity;
    for (const r of rows || []) {
      const t = parseT(r?.[f]);
      if (t < best) best = t;
    }
    result[f] = best;
  }
  return result;
}

export function NextRaceBanner({ event, split, now, compact = false }) {
  const target = new Date(`${event.date}T${event.time}:00`);
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);

  return (
    <div className={`relative overflow-hidden border border-white/10 ${compact ? "p-5" : "p-6 md:p-8"} bg-gradient-to-br from-[#141A26] to-[#0B0F19]`}
      data-testid={`next-race-${split.key}`}>
      <div className="absolute inset-0 diag-stripes pointer-events-none" />
      <div className="relative z-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="flex items-center flex-wrap gap-2">
            <div className="eyebrow" style={{ color: split.color }}>Nächstes Rennen · {split.name}</div>
            {event.is_sprint && (
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 font-bold" style={{ background: "#FFCC00", color: "#000" }} data-testid="sprint-badge-banner">Sprint</span>
            )}
          </div>
          <div className={`font-display font-black italic uppercase tracking-normal leading-none mt-2 ${compact ? "text-2xl" : "text-3xl md:text-5xl"}`}>
            {event.title}
          </div>
          <div className="flex flex-wrap gap-4 mt-3 text-sm text-neutral-300">
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" style={{ color: split.color }} />{event.track}</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" style={{ color: split.color }} />{formatDate(event.date)}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" style={{ color: split.color }} />{event.time} Uhr</span>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2 md:gap-3">
          {[["Tage", days], ["Std", hours], ["Min", minutes], ["Sek", seconds]].map(([l, v]) => (
            <div key={l} className="text-center border border-white/10 px-3 py-2 min-w-[64px]" style={{ background: "rgba(0,0,0,0.4)" }}>
              <div className={`font-display font-black italic leading-none ${compact ? "text-2xl" : "text-3xl md:text-4xl"}`} style={{ color: split.color }}>
                {String(v).padStart(2, "0")}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-400 mt-1">{l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const formatDate = (iso) => {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};
